import React from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import ListItemButton from '@mui/material/ListItemButton';

import Iconify from '../../../theme/iconify/iconify';

import { mislecAPI } from '../../../util/axios';

function ForgotPassword({ open, onClose, setOpen, setMessage, setSeverity }) {
    const { t } = useTranslation();

    const [email, setEmail] = React.useState('');
    const [username, setUsername] = React.useState('');
    const [sent, setSent] = React.useState(false);

    const resetPassword = () => {
        if (username === '') {
            notification(t('forgotPassword.error.username'), 'warning');
            return;
        }

        if (email === '') {
            notification(t('forgotPassword.error.email'), 'warning');
            return;
        }

        //  Data preparation
        const data = {
            email: email,
            username: username
        }

        //  Send data to server
        mislecAPI.put('/api/users/forgot/password', data)
            .then((res) => {
                setSent(true);

                //  Notify user of success
                setOpen(true);
                setMessage(t(res.data.detail));
                setSeverity('success');
            })
            .catch((error) => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);
            });
    }

    const notification = (message, severity) => {
        setMessage(message);
        setSeverity(severity);
        setOpen(true);
    }

    React.useEffect(() => {
        if (!open) {
            //  Reset modal
            setEmail('');
            setUsername('');
            setSent(false);
        }
    }, [open]);

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: { xs: '100%', md: '50%' }
                }}
            >
                <Paper
                    sx={{
                        padding: '24px',
                        minWidth: { xs: '80%', md: '35%' },
                        maxWidth: { xs: '95%', md: '35%' }
                    }}
                >
                    {!sent &&
                        <>
                            <Stack alignItems='center' justifyContent='center' spacing={1} mb={2}>
                                <Iconify icon='ic:round-password' width={40} height={40} sx={{ color: 'primary.main' }} />
                                <Typography variant='h6' sx={{ fontSize: '1.25rem !important' }}>
                                    {t('forgotPassword.title')}
                                </Typography>
                                <Typography variant='body2' sx={{ textAlign: 'center' }}>
                                    {t('forgotPassword.description')}
                                </Typography>
                                <Grid container sx={{ mt: '16px !important', mb: '8px !important' }}>
                                    <Grid item md={6} xs={12} sx={{ pr: { md: 1 } }}>
                                        <TextField
                                            id='mISLec_username'
                                            name='username'
                                            label={t('forgotPassword.inputs.username.label')}
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item md={6} xs={12}>
                                        <TextField
                                            id='mISLec_email'
                                            name='email'
                                            label={t('forgotPassword.inputs.email.label')}
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            fullWidth
                                            sx={{ mt: { xs: '16px !important', md: '0px !important' } }}
                                        />
                                    </Grid>
                                </Grid>
                            </Stack>
                            <Stack sx={{ mb: 2 }}>
                                <LoadingButton
                                    fullWidth
                                    size='large'
                                    type='submit'
                                    variant='contained'
                                    color='inherit'
                                    onClick={() => resetPassword()}
                                >
                                    {t('forgotPassword.buttons.reset')}
                                </LoadingButton>
                            </Stack>
                        </>
                    }
                    {sent &&
                        <>
                            <Grid container sx={{ mb: 2, mt: 1 }}>
                                <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Iconify icon='iconoir:send-mail' width={40} height={40} sx={{ color: 'primary.main' }} />
                                </Grid>
                                <Grid item xs={12} md={11} sx={{ pl: 3 }}>
                                    <Typography variant='h6' sx={{ mb: 1, fontSize: '1.25rem !important' }}>
                                        {t('forgotPassword.instructions.title')}
                                    </Typography>
                                    <Typography variant='body2'>
                                        {t('forgotPassword.instructions.pre')}
                                        <a href={`mailto:${email}`}>
                                            {email}
                                        </a>
                                        {t('forgotPassword.instructions.description')}
                                    </Typography>
                                    <Typography variant='body2' sx={{ mt: 2, color: 'text.secondary' }}>
                                        <span style={{ fontWeight: 'bold' }}>{t('forgotPassword.instructions.note')}</span>
                                    </Typography>
                                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                        <span style={{ fontStyle: 'italic' }}>{t('forgotPassword.instructions.junk')}</span>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </>
                    }
                    <Stack alignItems='center' justifyContent='center'>
                        <ListItemButton
                            disableRipple
                            sx={{
                                borderRadius: '8px',
                                padding: '6px',
                                '&:hover': {
                                    backgroundColor: 'rgba(145, 158, 171, 0.08)'
                                }
                            }}
                            onClick={() => onClose()}
                        >
                            <Iconify icon='ic:round-arrow-back' sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant='h6' sx={{ fontSize: '14px !important', color: 'text.secondary' }}>{t('forgotPassword.buttons.back')}</Typography>
                        </ListItemButton>
                    </Stack>
                </Paper>
            </Modal>
        </>
    );
}

export default ForgotPassword;